import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "../usuarios/usuarios_style.css";
import ScrollToTop from "../../../../components/scrollToTop/scrollToTop";
import { Modal, Button, Table } from "react-bootstrap";
import { FaArrowAltCircleDown } from "react-icons/fa";
import DOMPurify from "dompurify";

function ReporteVuelos() {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataPerPage, setDataPerPage] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPerPage] = useState(5);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const filteredData = data.filter((item) => {
    return searchValue
      ? item.awb.toUpperCase().includes(searchValue.toUpperCase()) ||
          item.no_flight.toUpperCase().includes(searchValue.toUpperCase()) ||
          item.route.toUpperCase().includes(searchValue.toUpperCase())
      : true;
  });

  const indexOfLastData = currentPage * dataPerPage;
  const indexOfFirstData = indexOfLastData - dataPerPage;

  const currentData = filteredData.slice(indexOfFirstData, indexOfLastData);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const totalPageCount = Math.ceil(filteredData.length / dataPerPage);
  const pageNumbers = [];
  const maxPageNumbersToShow = 20;

  let startPage, endPage;
  if (totalPageCount <= maxPageNumbersToShow) {
    startPage = 1;
    endPage = totalPageCount;
  } else {
    startPage = Math.max(1, currentPage - Math.floor(maxPageNumbersToShow / 2));
    endPage = Math.min(totalPageCount, startPage + maxPageNumbersToShow - 1);

    if (endPage - startPage < maxPageNumbersToShow) {
      startPage = endPage - maxPageNumbersToShow + 1;
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  //Historial de descargas
  const indexOfLastHistory = historyPage * historyPerPage;
  const indexOfFirstHistory = indexOfLastHistory - historyPerPage;
  const currentHistoryData = historyData.slice(
    indexOfFirstHistory,
    indexOfLastHistory
  );
  const totalHistoryPages = Math.ceil(historyData.length / historyPerPage);

  const showEntries = (e) => {
    setDataPerPage(e.target.value);
  };

  const handleSearch = async () => {
    setCurrentPage(1);

    const hasStart = !!startDate;
    const hasEnd = !!endDate;

    if (hasStart && !hasEnd) {
      Swal.fire("Error", "Debes seleccionar la fecha de fin", "error");
      return;
    }

    if (!hasStart && hasEnd) {
      Swal.fire("Error", "Debes seleccionar la fecha de inicio", "error");
      return;
    }

    if (hasStart && hasEnd) {
      if (new Date(startDate) > new Date(endDate)) {
        Swal.fire(
          "Error",
          "La fecha de inicio no puede ser mayor que la de fin",
          "error"
        );
        return;
      }

      if (!validateDateRange(startDate, endDate)) {
        Swal.fire(
          "Error",
          "El rango de fechas no puede ser mayor a 31 días",
          "error"
        );
        return;
      }

      await getData(true);
      return;
    }

    // Si no hay fechas ni búsqueda
    Swal.fire("Error", "Selecciona un rango de fechas", "error");
  };

  const formatDateForAPI = (dateString, isStart = true) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day)); // UTC exacta

    if (isStart) {
      return `${date.toISOString().split("T")[0]} 00:00:00`;
    } else {
      return `${date.toISOString().split("T")[0]} 23:59:59`;
    }
  };

  const validateDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
  };

  const getData = async (withDates = false) => {
    try {
      let url = `${process.env.REACT_APP_URL_BACKEND}/api/v1/reports/flights`;

      if (withDates && startDate && endDate) {
        if (!validateDateRange(startDate, endDate)) {
          Swal.fire(
            "Error",
            "El rango de fechas no puede ser mayor a 31 días",
            "error"
          );
          return;
        }
        const formattedStart = formatDateForAPI(startDate, true);
        const formattedEnd = formatDateForAPI(endDate, false);
        url += `?date_init=${formattedStart}&date_finish=${formattedEnd}`;
      }

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        },
      });

      if (res.data.code === 1) {
        // Ordenar de más nuevo a más viejo
        const sorted = res.data.data.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });

        setData(sorted);
      }
    } catch (error) {
      Swal.fire("Error", "Hubo un error", "error");
    }
  };

  const clearDateFilter = () => {
    setStartDate("");
    setEndDate("");
    getData();
    setCurrentPage(1);
  };

  const formatDateUTC = (dateString) => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const seconds = String(date.getUTCSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const formatDateHuman = (dateString) => {
    const date = new Date(dateString);

    return new Intl.DateTimeFormat("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC", // clave: mostrar igual para todos sin importar zona
    }).format(date);
  };

  const dowloadExcel = async () => {
    const hasStart = !!startDate;
    const hasEnd = !!endDate;

    if (!hasStart && !hasEnd) {
      Swal.fire(
        "Error",
        "Debes seleccionar las fechas de inicio y fin",
        "error"
      );
      return;
    }

    if (hasStart && !hasEnd) {
      Swal.fire("Error", "Debes seleccionar la fecha de fin", "error");
      return;
    }

    if (!hasStart && hasEnd) {
      Swal.fire("Error", "Debes seleccionar la fecha de inicio", "error");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      Swal.fire(
        "Error",
        "La fecha de inicio no puede ser mayor que la de fin",
        "error"
      );
      return;
    }

    const body = {
      taskType: "generateFlightsReportExcel",
      payload: {
        start_date: formatDateForAPI(startDate, true),
        end_date: formatDateForAPI(endDate, false),
      },
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_URL_BACKEND}/api/v1/cronjob/testSqs`,
        body,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );
      Swal.fire("Creado!", "El reporte se está generando.", "success");
    } catch (error) {
      console.error(error);
    }
  };

  const openHistoryModal = () => {
    setShowHistoryModal(true);
    fetchHistoryData();
  };

  const fetchHistoryData = async () => {
    setIsLoadingHistory(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/api/v1/reports/flights/files`,
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("token")}`,
          },
        }
      );

      if (res.data.code === 1) {
        setHistoryData(res.data.data);
      }
    } catch (error) {
      Swal.fire("Error", "No se pudo cargar el historial", "error");
    } finally {
      setIsLoadingHistory(false);
    }
  };

  function getFileNameFromPath(path) {
    return path.replace("gcp/flights/", "");
  }

  const safeURL = (url) => {
    if (typeof url !== 'string' || url.trim() === '') {
      return '#';
    }
    try {
      const parsed = new URL(url);

      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
        return '#';
      }

      const cleaned = DOMPurify.sanitize(parsed.href);
      return cleaned;
    } catch (e) {
      return "#";
    }
  };

  return (
    <>
      <ScrollToTop />
      <div className="table-responsive">
        <div
          className="dataTables_wrapper dt-bootstrap4 no-footer m-1"
          id="datatables_wrapper"
        >
          <div className="row justify-content-between mb-2">
            <div className="col-sm-12 col-md-2">
              <div className="dataTables_length" id="dataTables_length">
                <label className="d-flex justify-content-end">
                  <span className="mr-2 p-2">Mostrar</span>
                  <select
                    name="datatables_length"
                    aria-controls="datatables_length"
                    className="custom-select custom-select-sm form-control-sm narrow-select p-2"
                    onChange={showEntries}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="ml-2 p-2">Registros</span>
                </label>
              </div>
            </div>
            <div className="col-sm-12 col-md-9">
              <div className="datatables_filter" id="datatables_filter">
                <label className="d-flex justify-content-end">
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-control form-control-sm"
                    placeholder
                    aria-controls="datatables"
                  />

                  <span className="mr-2 mt1 p-2"></span>

                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="form-control form-control-sm"
                    placeholder
                    aria-controls="datatables"
                  />

                  <button className="btn-amc-blue ml-2" onClick={handleSearch}>
                    Buscar
                  </button>
                  {(startDate || endDate) && (
                    <button
                      className="btn-amc-pink ml-2"
                      onClick={clearDateFilter}
                    >
                      Limpiar
                    </button>
                  )}
                  <button
                    className="btn-amc-pink ml-2"
                    onClick={dowloadExcel}
                    disabled={!startDate || !endDate}
                    style={
                      !startDate || !endDate
                        ? { cursor: "not-allowed", backgroundColor: "gray" }
                        : {}
                    }
                  >
                    Exportar
                  </button>
                  <button
                    className="btn-amc-turq ml-2"
                    onClick={openHistoryModal}
                  >
                    Historial
                  </button>
                </label>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <table
                className="table table-inverse table-hover table-striped table-bordered dataTable no-footer text-center"
                role="grid"
                aria-describedby="datatables_info"
              >
                <thead>
                  <tr role="row">
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "auto" }}
                    >
                      Vuelo
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "200px" }}
                    >
                      Fecha
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "auto" }}
                    >
                      Origen
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "auto" }}
                    >
                      Destino
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "auto" }}
                    >
                      Código
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "auto" }}
                    >
                      Contabilizado
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "auto" }}
                    >
                      Timbrado
                    </th>
                    <th
                      className="sorting"
                      tabIndex={0}
                      aria-controls="datatables"
                      rowSpan={1}
                      colSpan={1}
                      style={{ width: "auto" }}
                    >
                      Mensaje
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.length > 0 ? (
                    currentData.map((flight) => (
                      <tr key={flight.id}>
                        <td>{flight.flight}</td>
                        <td>{formatDateUTC(flight.date)}</td>
                        <td>{flight.origin}</td>
                        <td>{flight.destination}</td>
                        <td>{flight.code}</td>
                        <td>
                          {flight.contabilizador_at
                            ? flight.contabilizador_at
                            : "---"}
                        </td>
                        <td>
                          {flight.timbrado_at ? flight.timbrado_at : "---"}
                        </td>
                        <td>{flight.message}</td>
                      </tr>
                    ))
                  ) : (
                    <td colSpan="11" className="text-center">
                      No hay datos
                    </td>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="row">
            <nav aria-label="Page navigation example">
              <ul className="pagination">
                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Anterior
                  </button>
                </li>
                {pageNumbers.map((pageNumber) => (
                  <li className="page-item" key={pageNumber}>
                    <button
                      className="page-link"
                      onClick={() => paginate(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  </li>
                ))}
                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === pageNumbers.length}
                  >
                    Siguiente
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      <Modal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Historial de Registros</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingHistory ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only"></span>
              </div>
              <p>Cargando historial...</p>
            </div>
          ) : historyData.length === 0 ? (
            <p className="text-center">No hay registros en el historial</p>
          ) : (
            <>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Creado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentHistoryData.map((item, _index) => (
                    <tr key={_index}>
                      <td>{getFileNameFromPath(item.key)}</td>
                      <td>{formatDateHuman(item.lastModified)}</td>
                      <td>
                        <a
                          href={safeURL(item.url)}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="primary" size="sm">
                            <FaArrowAltCircleDown />
                          </Button>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <nav className="d-flex justify-content-center mt-3">
                <ul className="pagination">
                  <li
                    className={`page-item ${
                      historyPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setHistoryPage(historyPage - 1)}
                      disabled={historyPage === 1}
                    >
                      Anterior
                    </button>
                  </li>
                  {Array.from(
                    { length: totalHistoryPages },
                    (_, i) => i + 1
                  ).map((pageNum) => (
                    <li
                      key={pageNum}
                      className={`page-item ${
                        historyPage === pageNum ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setHistoryPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  ))}
                  <li
                    className={`page-item ${
                      historyPage === totalHistoryPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setHistoryPage(historyPage + 1)}
                      disabled={historyPage === totalHistoryPages}
                    >
                      Siguiente
                    </button>
                  </li>
                </ul>
              </nav>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowHistoryModal(false)}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ReporteVuelos;
